/**
 * Feb 2026 — Brand configuration (frontend).
 *
 * Single source of truth for the customer-facing brand IDENTITY across
 * the entire frontend. Every visible mention of the business name,
 * logo, tagline and social-preview image should read from here.
 *
 * Rebrand safety contract
 * ────────────────────────
 * • This file governs BRAND WORDING and LOGO/IMAGE URLS only.
 * • It DOES NOT govern:
 *     - The operating domain (`sunshinecoastwatertesting.com.au`
 *       keeps serving as the primary origin — both domains point at
 *       the same app).
 *     - The `SCWT-XXXX` booking/sample/certificate identifier prefix
 *       (2,261 historic customer records depend on it).
 *     - The email sending address itself (`lab@sunshinecoastwatertesting.com.au`
 *       stays as the real FROM — only the friendly display name is
 *       "Aware Water Testing"; see backend/brand_config.py).
 *     - env vars, test-ids, DB fields, tracking URLs.
 * • A future domain-cutover would touch `homeUrl` here plus the
 *   equivalent on the backend, and add 301 redirects — NOT change any
 *   identifiers.
 *
 * See /app/memory/MIGRATION.md for the full guardrails.
 */

export const BRAND = {
  /**
   * Primary visible brand name. Replaces every prior "Sunshine Coast
   * Water Testing" mention in customer-facing surfaces (header,
   * footer, page copy, emails, PDF headers, SEO titles, OG tags).
   */
  name: "Aware Water Testing",

  /**
   * Long-form legal / descriptive name that expands the AWARE acronym.
   * Used in the footer disclaimer, compliance blocks and structured
   * data. Kept separate so short-form headlines can stay tight.
   */
  fullName: "Aware Water Testing — Australian Water Analysis, Reporting & Evaluation",

  /**
   * Short/compact form for tight spaces (mobile nav, tab titles).
   * Deliberately NOT an acronym — we don't want to swap "SCWT" for
   * "AWT" as a bare token because it becomes ambiguous. If we ever
   * need a compact tag we'll use "Aware".
   */
  short: "Aware",

  /**
   * Migration tagline — a subtle line in the footer for the first
   * 6–12 months to help returning customers connect the old identity
   * to the new one. Kept as a separate field so we can drop it in
   * one edit when we're ready.
   */
  formerly: "",

  /**
   * The pre-Feb-2026 trading name, ALWAYS populated.
   *
   * Jun 2026: Google AI Mode was surfacing "Sunshine Coast Water
   * Testing" as a SEPARATE business because nothing on the site linked
   * the two names. This field is the rebrand-continuity signal and is
   * used by schema.org `alternateName` plus the About and legal pages.
   * Kept separate from `formerly` (which stays empty) so the header and
   * footer remain clean per the user's decision.
   */
  formerName: "Sunshine Coast Water Testing",

  /**
   * The new business's primary domain. Preview environments serve
   * this AND the legacy domain — both point at the same app. This
   * value is used ONLY for external references (marketing copy,
   * schema.org URLs) not for internal API routing.
   */
  newDomain: "awarewatertesting.com.au",

  /**
   * Legacy domain — kept alive indefinitely so historic customer
   * tracking links (`/track/SCWT-XXXX`) never break.
   */
  legacyDomain: "sunshinecoastwatertesting.com.au",

  /**
   * The absolute URL used for `og:url`, canonical tags, structured
   * data `url`, JSON-LD `@id` bases. We keep this pointed at the
   * legacy domain so Google preserves rankings and historic
   * references keep resolving. See MIGRATION.md.
   */
  homeUrl: "https://awarewatertesting.com.au",

  /**
   * Logo assets — served from `/brand/` on the app's own domain so
   * they load fast and don't depend on external CDN URLs surviving.
   *
   * `primary` is the horizontal AWARE wordmark (droplet+microscope+
   * molecule mark on the left, "AWARE / Water Testing" wordmark on
   * the right). Served as .webp for the frontend (127KB) with a PNG
   * derivative sitting next to it for email/PDF use where webp is
   * still not universally supported.
   */
  logos: {
    // Frontend / browser display (webp, ~130KB, 2000×667).
    primary: "/brand/aware-water-testing-logo.webp",
    // PNG derivative for email clients / print / PDFs.
    primaryPng: "/brand/aware-water-testing-logo.png",
    // Historic transparent variant (kept for backwards compat with
    // any callers still importing it).
    transparent: "/brand/aware-water-testing-trans.png",
  },

  /**
   * Default social preview / OG image. Kept pointing at the existing
   * `og-default.jpg` served from the legacy domain so historic
   * shares don't 404. Replace once we generate an AWARE-branded
   * 1200×630.
   */
  ogImage: "https://awarewatertesting.com.au/img/v2/og-default.jpg",

  /**
   * Jun 2026 — official brand profiles, emitted as schema.org `sameAs`
   * on the Organization and LocalBusiness nodes. These are the signals
   * Google uses to consolidate the site, the Business Profile and the
   * social accounts into ONE entity ("Aware Water Testing") rather than
   * treating the old trading name as a separate business.
   * Only add URLs the business actually controls.
   */
  socials: [
    "https://www.facebook.com/awarewatertesting",
    "https://www.instagram.com/awarewatertesting/",
    "https://www.linkedin.com/company/aware-water-testing",
    "https://share.google/aaUPQYCHZT6DvtUmj",
  ],
};

/**
 * Convenience export for JSX text nodes where we want the migration
 * hint appended inline (e.g. `<footer>© Aware Water Testing, formerly Sunshine Coast Water Testing.</footer>`).
 */
export const BRAND_WITH_FORMERLY = `${BRAND.name}, ${BRAND.formerly}`;
